import {subDays} from 'date-fns';
import extend from 'tricks/object/extend';
import languageCodeMap from 'utils/languageCodeMap';
import parseSearch, {orOnly, parseFullTextSearch} from 'utils/parseSearch';

export function parseUserSearchInput(input, q) {
	const userDomains = {};

	if (!q.filter) {
		q.filter = {};
	}

	const {filter} = q;

	let suffixConditions = 0;

	// Anonymous text to search
	const hash = parseSearch(input, {
		text(values, negate) {
			filter['*textsearch'] = parseFullTextSearch(
				values,
				negate,
				filter['*textsearch']
			);
		},

		email(values, negate) {
			filter[`${negate}email$${++suffixConditions}`] = values;
		},

		// is:admin, not:admin
		admin(_, negate) {
			userDomains[`${!negate ? '-' : ''}role_id`] = 0;
		},

		team_id(values, negate) {
			let team = {
				id: orOnly(values),
			};

			if (values.length === 0) {
				team = {'-id': null};
			}

			extend(filter, {
				[`${negate}userTeams$${++suffixConditions}`]: {team},
			});
		},

		team(values, negate) {
			let team = {
				name: orOnly(values),
			};

			if (values.length === 0) {
				team = {'-id': null};
			}

			extend(filter, {
				[`${negate}userTeams$${++suffixConditions}`]: {team},
			});
		},

		role(values) {
			userDomains.role = {name: values};
		},

		employeeid(values, negate) {
			if (values.length) {
				userDomains[`${negate}employeeid`] = orOnly(values);
			} else {
				userDomains[`${!negate ? '-' : ''}employeeid`] = null;
			}
		},
		employee_id(values, negate) {
			this.employeeid(values, negate);
		},
		manager(values, negate) {
			const condition = {
				role: 'manager',
			};

			if (values.length) {
				condition.team = {
					name: orOnly(values),
				};
			}

			// Ensure they are a manager, or not
			extend(filter, {
				[`${negate}userTeams$${++suffixConditions}`]: condition,
			});
		},

		playlistcomplete(values, negate) {
			// Filter users who have completed the following playlists (negate: or not completed)
			extend(filter, {
				[`${negate}userCollections`]: {
					completion_rate: 1,
					collection_id: values,
				},
			});
		},
		playlistsaved(values, negate) {
			extend(filter, {
				[`${negate}userCollections`]: {
					saved: 1,
					collection_id: values,
				},
			});
		},

		deleted(values, negate) {
			if (values.length) {
				userDomains[`${negate}is_deleted`] = orOnly(values);
			} else {
				userDomains.is_deleted = !negate;
			}
		},

		created([value]) {
			userDomains.created_time = value;
		},

		updated([value]) {
			userDomains.updated_time = value;
		},

		activity(values, negate) {
			if (values.length === 0) {
				userDomains[`${!negate ? '-' : ''}last_activity_time`] = null;
			} else {
				userDomains[`${negate}last_activity_time`] = orOnly(values);
			}
		},

		inactive(values, negate) {
			this.activity(values, !negate ? '-' : '');
		},

		loginsincedays([value], negate) {
			const date = subDays(new Date(), value);
			extend(filter, {
				userDomains: {
					[`${negate}last_activity_time`]: `${date
						.toISOString()
						.split('T')
						.at(0)}..`,
				},
			});
		},
		language(values, negate) {
			if (values.length) {
				extend(filter, {
					[`${negate}language`]: orOnly(values.map(languageCodeMap)),
				});
			} else {
				extend(filter, {[`${!negate ? '-' : ''}language`]: null});
			}
		},

		session([created_time]) {
			extend(filter, {session: {created_time}});
		},

		openedasset(values) {
			// Filter users who have opened a particular asset
			filter.session = Object.assign(filter.session || {}, {
				events: {
					action: 'open',
					category: 'asset',
					ref_id: values,
				},
			});
		},

		invited(_, negate) {
			extend(filter, {userDomains: {is_invited: !negate}});
		},

		sort(values) {
			const sort = values
				.map(str => {
					const a = str.split('-');
					const r = {
						activity: ['userDomains.last_activity_time', 'desc'],
						active: ['userDomains.last_activity_time', 'desc'],
						updated: ['users.updated_time', 'desc'],
						created: ['users.created_time', 'desc'],
						name: ['users.name', 'asc'],
						id: ['users.id', 'desc'],
					}[a[0]];

					if (!r) {
						return null;
					}

					// overide direction
					if (a[1]) {
						r[1] = a[1];
					}

					return r.join(' ');
				})
				.filter(str => !!str);

			if (sort.length) {
				q.orderby = sort.join(',');
			}
		},

		default(values, negate, key) {
			// filter by anything else...
			q.filter[`${negate}${key}`] = orOnly(values);
		},
	});

	if (Object.keys(userDomains).length) {
		filter.userDomains = Object.assign(
			filter.userDomains || {},
			userDomains
		);
	}

	return hash;
}

export function parseUserSearch(search) {
	const apiQuery = {};
	parseUserSearchInput(search, apiQuery);
	return apiQuery.filter;
}
